import type { JumpChart } from '@shared/jumpChart';
import {
  decodeJumpGrid,
  JUMP_GROUND_ROWS,
  JUMP_GROUND_Y,
  type JumpTerrainSpan,
} from '@shared/jumpGrid';
import type { JumpLayout } from '../jumpPlayfieldGeometry';
import { canvasXToBeat, obstacleCanvasX, playfieldYToCanvas } from '../jumpPlayfieldGeometry';
import type { RenderQuality } from '../../rendering/renderQuality';
import {
  BEAT_PULSE_STRENGTH,
  COMBO_GROUND_GLOW_BONUS,
  STAR_COUNT,
  STAR_DRIFT_FRAC_PER_BEAT,
  TERRAIN_DROP_EDGE_ALPHA,
  TERRAIN_FACE_ACCENT_WIDTH,
  TERRAIN_FACE_LETHAL_ALPHA,
} from './jumpRenderConstants';
import { hexToRgba } from './jumpRenderColour';

interface Star {
  normX: number;
  normY: number;
  size: number;
  alpha: number;
}

/**
 * Decoded terrain padded with synthetic floor spans before the first column
 * (beat 0) and after the last, so the start and end of every map always show
 * ground rather than a void. The cube is anchored partway across the playfield,
 * so without this the area left of beat 0 — including everything under the cube
 * during the lead-in countdown, when the audio clock is still negative — and the
 * area right of the final beat render as empty space.
 *
 * Render-only: the engine's collision surface is unchanged, and the cached
 * decode array is never mutated (we copy before inserting). The leading span
 * inherits the first span's height (column 0 is always solid floor); the
 * trailing span inherits the last span's height, falling back to baseline ground
 * if the map happens to end on a pit so the run still finishes on ground.
 */
function terrainWithGroundRunout(
  terrain: ReadonlyArray<JumpTerrainSpan>,
  layout: JumpLayout,
  beat: number,
  visibleBeats: number,
  canvasWidth: number,
): JumpTerrainSpan[] {
  const spans = [...terrain];

  const first = terrain[0];
  if (obstacleCanvasX(layout, beat, first.startBeat, visibleBeats) > 0) {
    spans.unshift({
      startBeat: canvasXToBeat(layout, beat, 0, visibleBeats),
      endBeat: first.startBeat,
      topY: first.topY,
      pit: false,
      floorRows: first.floorRows,
    });
  }

  const last = terrain[terrain.length - 1];
  if (obstacleCanvasX(layout, beat, last.endBeat, visibleBeats) < canvasWidth) {
    spans.push({
      startBeat: last.endBeat,
      endBeat: canvasXToBeat(layout, beat, canvasWidth, visibleBeats),
      topY: last.pit ? JUMP_GROUND_Y : last.topY,
      pit: false,
      floorRows: last.pit ? JUMP_GROUND_ROWS : last.floorRows,
    });
  }

  return spans;
}

/**
 * Owns cached background gradients + a pre-built star field, and paints the
 * scrolling ground. All render-only state — the scene pass never touches engine
 * data beyond the chart's ground line.
 */
export class JumpSceneRenderer {
  private bgGradient: CanvasGradient | null = null;
  private bgGradientKey = '';
  private readonly stars: Star[] = [];
  private readonly quality: RenderQuality;

  constructor(quality: RenderQuality) {
    this.quality = quality;
    this.initStars(quality.decorations ? STAR_COUNT : 0);
  }

  invalidate(): void {
    this.bgGradient = null;
    this.bgGradientKey = '';
  }

  draw(
    ctx: CanvasRenderingContext2D,
    layout: JumpLayout,
    chart: JumpChart,
    beat: number,
    visibleBeats: number,
    canvasWidth: number,
    canvasHeight: number,
    beatPulse: number,
    comboEnergy: number,
  ): void {
    const palette = chart.palette;

    const key = `${canvasHeight}|${palette.bg}|${palette.bgAccent}`;
    if (!this.bgGradient || this.bgGradientKey !== key) {
      const grad = ctx.createLinearGradient(0, 0, 0, canvasHeight);
      grad.addColorStop(0, palette.bg);
      grad.addColorStop(0.7, palette.bgAccent);
      grad.addColorStop(1, palette.bg);
      this.bgGradient = grad;
      this.bgGradientKey = key;
    }
    ctx.fillStyle = this.bgGradient;
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    if (this.stars.length > 0) {
      this.drawStars(ctx, beat, canvasWidth, canvasHeight, palette.ui, beatPulse);
    }

    this.drawTerrain(
      ctx,
      layout,
      chart,
      beat,
      visibleBeats,
      canvasWidth,
      canvasHeight,
      beatPulse,
      comboEnergy,
    );
  }

  private initStars(count: number): void {
    this.stars.length = 0;
    for (let i = 0; i < count; i += 1) {
      const seed = (i * 9301 + 49297) % 233280;
      const ySeed = (i * 4096 + 7841) % 233280;
      const sizeSeed = (i * 13 + 31) % 100;
      this.stars.push({
        normX: (seed / 233280) * 2,
        // Keep stars in the upper sky so they don't sit behind the ground band.
        normY: (ySeed / 233280) * 0.7,
        size: 0.6 + (sizeSeed / 100) * 1.8,
        alpha: 0.18 + (sizeSeed / 100) * 0.4,
      });
    }
  }

  private drawStars(
    ctx: CanvasRenderingContext2D,
    beat: number,
    canvasWidth: number,
    canvasHeight: number,
    starColour: string,
    beatPulse: number,
  ): void {
    ctx.save();
    ctx.fillStyle = starColour;
    const wrapWidth = canvasWidth * 1.4;
    const drift = (beat * canvasWidth * STAR_DRIFT_FRAC_PER_BEAT) % wrapWidth;
    const pulseBoost = 1 + beatPulse * BEAT_PULSE_STRENGTH * 0.4;
    const pulseAlpha = 0.85 + beatPulse * 0.15;
    for (const star of this.stars) {
      const baseX = star.normX * canvasWidth;
      const x = (((baseX - drift) % wrapWidth) + wrapWidth) % wrapWidth - canvasWidth * 0.2;
      const y = star.normY * canvasHeight;
      ctx.globalAlpha = star.alpha * pulseAlpha;
      ctx.beginPath();
      ctx.arc(x, y, star.size * pulseBoost, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }

  /**
   * Tile terrain: a solid block per terrain span at its own floor-top height
   * (baseline ground, raised platforms), a glowing landable top edge, scrolling
   * hatch texture, pit chasms left as voids, and hazard-coloured accents on the
   * lethal step-up / pit-cliff faces (and a dimmer edge on a pit's drop-off).
   * Every span scrolls through the shared {@link obstacleCanvasX} mapping so the
   * terrain and hazards move as one. Terrain comes from the grid's deterministic
   * decode, so visuals and the engine's surface model can never diverge.
   */
  private drawTerrain(
    ctx: CanvasRenderingContext2D,
    layout: JumpLayout,
    chart: JumpChart,
    beat: number,
    visibleBeats: number,
    canvasWidth: number,
    canvasHeight: number,
    beatPulse: number,
    comboEnergy: number,
  ): void {
    const palette = chart.palette;
    const { terrain } = decodeJumpGrid(chart.grid);
    const glow = Math.min(
      1,
      0.55 + beatPulse * BEAT_PULSE_STRENGTH + comboEnergy * COMBO_GROUND_GLOW_BONUS,
    );
    const chromeShadows = this.quality.chromeShadows;

    // Pad the decoded terrain with leading/trailing baseline floor so the cube
    // always rests on visible ground at the very start (during the lead-in
    // countdown, when the audio clock is still negative and real terrain hasn't
    // scrolled under the cube yet, and at beat 0) and the very end of the map,
    // rather than appearing to hover over a void.
    const spans = terrainWithGroundRunout(terrain, layout, beat, visibleBeats, canvasWidth);

    ctx.save();
    for (const span of spans) {
      if (span.pit) {
        continue;
      }
      const x0 = obstacleCanvasX(layout, beat, span.startBeat, visibleBeats);
      const x1 = obstacleCanvasX(layout, beat, span.endBeat, visibleBeats);
      if (x1 < -4 || x0 > canvasWidth + 4) {
        continue;
      }
      const topY = playfieldYToCanvas(layout, span.topY);
      const width = x1 - x0;

      // Solid body down to the playfield floor.
      ctx.fillStyle = hexToRgba(palette.bg, 0.92);
      ctx.fillRect(x0, topY, width, canvasHeight - topY);

      // Hatch texture every 2 beats, clipped to this span's body.
      ctx.save();
      ctx.beginPath();
      ctx.rect(x0, topY, width, canvasHeight - topY);
      ctx.clip();
      ctx.strokeStyle = hexToRgba(palette.ui, 0.1);
      ctx.lineWidth = 2;
      ctx.beginPath();
      const firstHatch = Math.ceil(span.startBeat / 2) * 2;
      for (let b = firstHatch; b < span.endBeat; b += 2) {
        const hx = obstacleCanvasX(layout, beat, b, visibleBeats);
        ctx.moveTo(hx, topY);
        ctx.lineTo(hx, canvasHeight);
      }
      ctx.stroke();
      ctx.restore();

      // Bright beat-synced landable top edge.
      ctx.strokeStyle = palette.player;
      ctx.lineWidth = 3;
      ctx.globalAlpha = glow;
      if (chromeShadows) {
        ctx.shadowColor = palette.player;
        ctx.shadowBlur = 12 + comboEnergy * 8;
      } else {
        ctx.shadowBlur = 0;
      }
      ctx.beginPath();
      ctx.moveTo(x0, topY);
      ctx.lineTo(x1, topY);
      ctx.stroke();
      ctx.globalAlpha = 1;
      ctx.shadowBlur = 0;
    }

    this.drawTerrainFaces(ctx, layout, terrain, beat, visibleBeats, canvasWidth, canvasHeight, palette);
    ctx.restore();
  }

  /**
   * Vertical accents at terrain boundaries: a lethal hazard stripe down a
   * step-up wall or a pit cliff (the engine's block-face), and a dimmer danger
   * edge at a pit's near drop-off (falling there is the lethal part, not a wall).
   * A step DOWN between solid spans is a safe ledge and gets no accent.
   */
  private drawTerrainFaces(
    ctx: CanvasRenderingContext2D,
    layout: JumpLayout,
    terrain: ReadonlyArray<JumpTerrainSpan>,
    beat: number,
    visibleBeats: number,
    canvasWidth: number,
    canvasHeight: number,
    palette: JumpChart['palette'],
  ): void {
    const accentW = TERRAIN_FACE_ACCENT_WIDTH * layout.scaleX;
    for (let i = 1; i < terrain.length; i += 1) {
      const prev = terrain[i - 1];
      const cur = terrain[i];
      const x = obstacleCanvasX(layout, beat, cur.startBeat, visibleBeats);
      if (x < -accentW || x > canvasWidth + accentW) {
        continue;
      }
      if (!cur.pit && !prev.pit && cur.topY < prev.topY) {
        // Step up between solid spans: lethal wall from cur top to prev top.
        const top = playfieldYToCanvas(layout, cur.topY);
        const bottom = playfieldYToCanvas(layout, prev.topY);
        ctx.fillStyle = hexToRgba(palette.hazard, TERRAIN_FACE_LETHAL_ALPHA);
        ctx.fillRect(x, top, accentW, bottom - top);
      } else if (!cur.pit && prev.pit) {
        // Cliff up out of a pit: lethal wall from cur top to the chasm floor.
        const top = playfieldYToCanvas(layout, cur.topY);
        ctx.fillStyle = hexToRgba(palette.hazard, TERRAIN_FACE_LETHAL_ALPHA);
        ctx.fillRect(x, top, accentW, canvasHeight - top);
      } else if (cur.pit && !prev.pit) {
        // Drop-off into a pit: danger edge on the leaving ledge (safe to fall is
        // the death — the cube must clear the gap, not avoid a wall).
        const top = playfieldYToCanvas(layout, prev.topY);
        ctx.fillStyle = hexToRgba(palette.hazard, TERRAIN_DROP_EDGE_ALPHA);
        ctx.fillRect(x - accentW, top, accentW, canvasHeight - top);
      }
    }
  }
}
